// src/router/guards/PermissionGuard.tsx
// Route guard — restricts access to specific routes based on staff permission scopes.
import { Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '@/app/hooks'
import { useGetStaffPermissionScopeQuery } from '@/features/staff/staff.api'
import type { StaffPermission } from '@/features/staff/staff.types'
import { RingLoader } from 'react-spinners'

interface PermissionGuardProps {
  permission: StaffPermission
}

export default function PermissionGuard({ permission }: PermissionGuardProps) {
  const { user, isAuthenticated, isInitializing } = useAuth()
  const isSuperAdmin =
    user?.primaryRole?.includes('SUPER_ADMIN') ||
    user?.roles?.some((r) => r.includes('SUPER_ADMIN'))

  // Only fire the permissions API when we have a logged-in non-super-admin (i.e. a staff member)
  const { data: permissionsResponse, isLoading } = useGetStaffPermissionScopeQuery(undefined, {
    skip: isInitializing || !isAuthenticated || !!isSuperAdmin,
  })

  if (!isSuperAdmin && isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-neutral-50 dark:bg-[#06090d] transition-colors duration-150">
        <RingLoader color="#33cb98" size={80} />
      </div>
    )
  }

  const permissions = permissionsResponse?.data ?? []
  const hasPermission = isSuperAdmin || permissions.includes(permission)

  if (!hasPermission) {
    return <Navigate to="/unauthorized" replace />
  }

  return <Outlet />
}
